/**
 * The sector switchboard's config (the coordinator's row, Oct 10; the Lab's pattern, 67.5): one row per sector rule
 * that changes play, each with a one-line reading of what it changes, its default (DEFAULT_RULES — every rule stays
 * off unless a decision says otherwise), a saved copy in this browser (`orbituary-sector-rules`) and a URL form
 * (`?sector-rules=salvage,ammunition,-marksCrossDown`: names turn a rule on, a leading minus turns it off). The
 * rules apply to the next new sector (a sector keeps the rules it was made with; its save carries them). Pure: no
 * DOM; `sectorScreen.ts` draws the panel and `main.ts` reads storage.
 */
import { DEFAULT_RULES } from '../sector/sector';
import type { SectorRules } from '../sector/types';

export type SectorRuleKey =
  | 'battleModel' | 'surrogateFirst' | 'reserves' | 'extraction' | 'callIns' | 'marksCrossDown'
  | 'adaptiveGarrisons' | 'valueSystems' | 'stealPicture' | 'signatures' | 'commsReach' | 'satellites' | 'weatherWindows'
  | 'salvage' | 'ammunition' | 'decoys';

export interface SectorRuleRow { key: SectorRuleKey; label: string; group: 'battles' | 'the stand-in' | 'knowledge' | 'the world' | 'logistics'; reading: string }

/** Every switchable rule, in the order the panel shows them. */
export const SECTOR_RULE_ROWS: SectorRuleRow[] = [
  { key: 'battleModel', group: 'battles', label: 'Model battles', reading: 'battles nobody enters run in the fast battle model (minutes of sector time) instead of a full auto-resolved match' },
  { key: 'surrogateFirst', group: 'battles', label: 'Battles start at once', reading: 'a due battle starts in the model without waiting for you; several can run in one turn and you enter any of them (needs model battles: turns it on)' },
  { key: 'reserves', group: 'battles', label: 'Reserves arrive', reading: 'units held out by the troop cap come in as the side’s respawn waves, at its arrival lane' },
  { key: 'extraction', group: 'battles', label: 'Withdrawal is extraction', reading: 'full battles have exits; a side ordered out must reach where it landed and leave under fire' },
  { key: 'callIns', group: 'battles', label: 'Call-ins', reading: 'in full battles a unit can ask for the guns on a point it sees (CALL IN FIRE); a gun on another planet answers or the ask is refused' },
  { key: 'marksCrossDown', group: 'battles', label: 'Marks cross down', reading: 'a system you mark on the map (the mark order mode) carries into the next battle there as a mark or a fire mission' },
  { key: 'adaptiveGarrisons', group: 'the stand-in', label: 'Garrisons adapt', reading: 'a garrison remembers how it was attacked: watches for sneaks, faces the lane, refits' },
  { key: 'valueSystems', group: 'the stand-in', label: 'Systems have worth', reading: 'the enemy marches on what is worth taking (intact planets, what is built, a home), not only the nearest' },
  { key: 'stealPicture', group: 'knowledge', label: 'Steal their picture', reading: 'taking a garrisoned system (or its standing antenna or sensor) hands you its holder’s reports, dated' },
  { key: 'signatures', group: 'knowledge', label: 'Signatures', reading: 'battles and arrivals leave fading traces on systems (who fought, from which lane, how long they held); you learn them with the system' },
  { key: 'commsReach', group: 'knowledge', label: 'Comms reach', reading: 'orders and reports take half a day per lane from your home or a held antenna; past an enemy wall they are lost' },
  { key: 'decoys', group: 'knowledge', label: 'Decoys', reading: 'a squad can leave a decoy that reads as it to distant eyes (antenna, sensor, satellite) until a squad or a scout looks closely; the enemy leaves one when it sneaks out' },
  { key: 'satellites', group: 'knowledge', label: 'Satellite passes', reading: 'each side’s satellite looks at one system a day on a schedule everyone knows; the enemy times sneaks between passes' },
  { key: 'weatherWindows', group: 'the world', label: 'Weather windows', reading: 'each system has weather by day (with a forecast); a battle holds its day’s weather; fog, storm and dust hide a sneak' },
  { key: 'salvage', group: 'logistics', label: 'Salvage', reading: 'battles leave wrecks; a squad resting where you hold salvages them into supply' },
  { key: 'ammunition', group: 'logistics', label: 'Ammunition', reading: 'squads carry finite rounds, bought with supply at home or a depot, spent in full battles' },
];

export type SectorRulesConfig = Record<SectorRuleKey, boolean>;

export const SECTOR_RULES_STORAGE_KEY = 'orbituary-sector-rules';

/** The defaults: the sector's own (every rule as DEFAULT_RULES has it). */
export const SECTOR_RULES_DEFAULTS: SectorRulesConfig = Object.fromEntries(SECTOR_RULE_ROWS.map(r => [r.key, !!DEFAULT_RULES[r.key]])) as SectorRulesConfig;

/** A config from anything JSON-shaped: unknown or malformed fields fall back to the defaults. */
export function normalizeSectorRules(raw: unknown): SectorRulesConfig {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const out = { ...SECTOR_RULES_DEFAULTS };
  for (const row of SECTOR_RULE_ROWS) if (typeof r[row.key] === 'boolean') out[row.key] = r[row.key] as boolean;
  return out;
}

/** The rules a config sets (surrogate-first needs the model, so it turns the model on). */
export function rulesOf(c: SectorRulesConfig): Partial<SectorRules> {
  const out: Partial<SectorRules> = {};
  for (const row of SECTOR_RULE_ROWS) (out as Record<string, boolean>)[row.key] = c[row.key];
  if (c.surrogateFirst) out.battleModel = true;
  return out;
}

/** The URL form: only what differs from the defaults (`salvage,-marksCrossDown`); empty when nothing does. */
export function sectorRulesParam(c: SectorRulesConfig): string {
  return SECTOR_RULE_ROWS.filter(r => c[r.key] !== SECTOR_RULES_DEFAULTS[r.key]).map(r => (c[r.key] ? r.key : `-${r.key}`)).join(',');
}

/** Back from the URL form, on top of `base` (the defaults): unknown names are ignored. */
export function sectorRulesFromParam(param: string, base: SectorRulesConfig = SECTOR_RULES_DEFAULTS): SectorRulesConfig {
  const out = { ...base };
  const keys = new Set(SECTOR_RULE_ROWS.map(r => r.key as string));
  for (const raw of param.split(',').map(x => x.trim()).filter(Boolean)) {
    const off = raw.startsWith('-');
    const name = off ? raw.slice(1) : raw;
    if (keys.has(name)) out[name as SectorRuleKey] = !off;
  }
  return out;
}

/** How many rules are on beyond the defaults (the switchboard's badge). */
export function rulesOnCount(c: SectorRulesConfig): number {
  return SECTOR_RULE_ROWS.filter(r => c[r.key] && !SECTOR_RULES_DEFAULTS[r.key]).length;
}

/** The rules a sector actually runs, as names (for the panel's "this sector runs" line). */
export function rulesInForce(rules: SectorRules): string[] {
  return SECTOR_RULE_ROWS.filter(r => rules[r.key] && !SECTOR_RULES_DEFAULTS[r.key]).map(r => r.label);
}
